import { gql } from 'graphql-request';

import { gqlClient } from 'graphql/gqlClient';
import { InvitableCommunity, UserInvite } from 'types/invites.types';
import { Distrolist } from 'types/distrolist.types';

// ===== Types =====

export interface InviteGuestsInput {
  eventId: number;
  userIdsToInvite?: number[];
  distrolistIdsToInvite?: number[];
  communityIdsToInvite?: number[];
  sendCalendarEmail: boolean;
  sendSlackInvite: boolean;
  deleteCalEvent: boolean;
  currentInviteTotal: number;
  expectedEventInviteeTotal: number;
  hadDedupeHappened: boolean;
  isDontSendClicked: boolean;
}

export interface InviteGuestsResponse {
  inviteGuests: {
    ok: boolean;
    isAsync: boolean;
  };
}

export interface GetInvitableUsersInput {
  eventId?: number;
  searchText?: string;
  emails?: string[];
  page?: number;
  perPage?: number;
}

export interface GetInvitableUsersResponse {
  invitableUsers: UserInvite[];
}

export interface GetInvitableDistrolistsInput {
  searchText?: string;
  page?: number;
  perPage?: number;
}

export interface GetInvitableDistrolistsResponse {
  companyDistrolists: {
    list: Distrolist[];
    pages: number;
  };
}

export interface GetInvitableCommunitiesInput {
  eventId: number;
  searchText?: string;
  page?: number;
  perPage?: number;
}

export interface GetInvitableCommunitiesResponse {
  invitableCommunities: {
    list: InvitableCommunity[];
    total: number;
    pages: number;
  };
}

export interface GetInviteeTotalCountsInput {
  eventId: number;
  distrolistIds?: number[];
  communityIds?: number[];
  inviteeIds?: number[];
}

export interface GetInviteeTotalCountsResponse {
  eventInviteeTotalCounts: {
    currentInviteeTotal: number;
    expectedInviteeTotal: number;
    dedupeRequired: boolean;
    skippedDuplicateEmails: string[] | null;
  };
}

export interface GetLatestInvitationJobStatusInput {
  eventId: number;
}

export interface GetLatestInvitationJobStatusResponse {
  latestInvitationProcessingJobStatus: {
    jobStatus: string;
    numInvitees: number;
  } | null;
}

// ===== GraphQL Queries and Mutations =====

const INVITE_GUESTS_MUTATION = gql`
  mutation InviteGuests(
    $userIdsToInvite: [Int]
    $distrolistIdsToInvite: [Int]
    $communityIdsToInvite: [Int]
    $eventId: Int!
    $sendCalendarEmail: Boolean!
    $sendSlackInvite: Boolean!
    $deleteCalEvent: Boolean!
    $currentInviteTotal: Int!
    $expectedEventInviteeTotal: Int!
    $hadDedupeHappened: Boolean!
    $isDontSendClicked: Boolean!
  ) {
    inviteGuests(
      userIdsToInvite: $userIdsToInvite
      distrolistIdsToInvite: $distrolistIdsToInvite
      communityIdsToInvite: $communityIdsToInvite
      eventId: $eventId
      sendCalendarEmail: $sendCalendarEmail
      sendSlackInvite: $sendSlackInvite
      deleteCalEvent: $deleteCalEvent
      currentInviteTotal: $currentInviteTotal
      expectedEventInviteeTotal: $expectedEventInviteeTotal
      hadDedupeHappened: $hadDedupeHappened
      isDontSendClicked: $isDontSendClicked
    ) {
      ok
      isAsync
    }
  }
`;

const INVITABLE_USERS_QUERY = gql`
  query InvitableUsers(
    $eventId: Int
    $searchText: String
    $emails: [String]
    $page: Int
    $perPage: Int
  ) {
    invitableUsers(
      eventId: $eventId
      searchText: $searchText
      emails: $emails
      page: $page
      perPage: $perPage
    ) {
      id
      firstName
      lastName
      email
      isInvited
      currentStatus
    }
  }
`;

const COMPANY_DISTROLISTS_QUERY = gql`
  query CompanyDistrolists($searchText: String, $page: Int, $perPage: Int) {
    companyDistrolists(searchText: $searchText, page: $page, perPage: $perPage) {
      list {
        id
        name
        email
        description
        memberCount
      }
      pages
    }
  }
`;

const INVITABLE_COMMUNITIES_QUERY = gql`
  query InvitableCommunities($eventId: Int!, $searchText: String, $page: Int, $perPage: Int) {
    invitableCommunities(
      eventId: $eventId
      searchText: $searchText
      page: $page
      perPage: $perPage
    ) {
      list {
        id
        name
        privacy
        memberCount
      }
      total
      pages
    }
  }
`;

const EVENT_INVITEE_TOTAL_COUNTS_QUERY = gql`
  query EventInviteeTotalCounts(
    $eventId: Int!
    $distrolistIds: [Int]
    $communityIds: [Int]
    $inviteeIds: [Int]
  ) {
    eventInviteeTotalCounts(
      eventId: $eventId
      distrolistIds: $distrolistIds
      communityIds: $communityIds
      inviteeIds: $inviteeIds
    ) {
      currentInviteeTotal
      expectedInviteeTotal
      dedupeRequired
      skippedDuplicateEmails
    }
  }
`;

const LATEST_INVITATION_PROCESSING_JOB_STATUS_QUERY = gql`
  query LatestEventInvitationProcessingJobStatus($eventId: Int!) {
    latestInvitationProcessingJobStatus(eventId: $eventId) {
      jobStatus
      numInvitees
    }
  }
`;

// ===== API Functions =====

export const inviteGuestsApi = {
  invite: async (input: InviteGuestsInput): Promise<InviteGuestsResponse['inviteGuests']> => {
    return gqlClient
      .request<InviteGuestsResponse>(INVITE_GUESTS_MUTATION, input)
      .then((res) => res.inviteGuests);
  },

  getInvitableUsers: async (
    input: GetInvitableUsersInput
  ): Promise<GetInvitableUsersResponse['invitableUsers']> => {
    return gqlClient
      .request<GetInvitableUsersResponse>(INVITABLE_USERS_QUERY, input)
      .then((res) => res.invitableUsers);
  },

  getInvitableDistrolists: async (
    input: GetInvitableDistrolistsInput
  ): Promise<GetInvitableDistrolistsResponse['companyDistrolists']> => {
    return gqlClient
      .request<GetInvitableDistrolistsResponse>(COMPANY_DISTROLISTS_QUERY, input)
      .then((res) => res.companyDistrolists);
  },

  getInvitableCommunities: async (
    input: GetInvitableCommunitiesInput
  ): Promise<GetInvitableCommunitiesResponse['invitableCommunities']> => {
    return gqlClient
      .request<GetInvitableCommunitiesResponse>(INVITABLE_COMMUNITIES_QUERY, input)
      .then((res) => res.invitableCommunities);
  },

  getInviteeTotalCounts: async (
    input: GetInviteeTotalCountsInput
  ): Promise<GetInviteeTotalCountsResponse['eventInviteeTotalCounts']> => {
    return gqlClient
      .request<GetInviteeTotalCountsResponse>(EVENT_INVITEE_TOTAL_COUNTS_QUERY, input)
      .then((res) => res.eventInviteeTotalCounts);
  },

  getLatestInvitationJobStatus: async (
    input: GetLatestInvitationJobStatusInput
  ): Promise<GetLatestInvitationJobStatusResponse['latestInvitationProcessingJobStatus']> => {
    return gqlClient
      .request<GetLatestInvitationJobStatusResponse>(
        LATEST_INVITATION_PROCESSING_JOB_STATUS_QUERY,
        input
      )
      .then((res) => res.latestInvitationProcessingJobStatus);
  }
};
